import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';
import { EventIcon } from '../../utils/IconWrapper';
import Section from '../../components/ui/Section';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.div`
  background-image: url('https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const ServiceTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--accent-primary);
  }
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  svg {
    margin-top: 5px;
    margin-right: 1rem;
    min-width: 24px;
    color: var(--accent-primary);
  }
`;

const FeatureText = styled.div`
  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
  }
  
  p {
    color: var(--text-secondary);
    line-height: 1.6;
  }
`;

const CallToAction = styled.div`
  background-color: var(--bg-secondary);
  padding: 3rem;
  text-align: center;
  border-radius: 8px;
  margin: 3rem 0;
`;

const CTATitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const CTAText = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const Events: React.FC = () => {
  const { language } = useLanguage();
  
  return (
    <PageContainer>
      <Helmet>
        <title>
          {language === 'it' 
            ? 'Eventi | Servizi J.O.H.' 
            : 'Events | J.O.H. Services'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizi per eventi esclusivi in location di prestigio: pianificazione e organizzazione di eventi privati, aziendali e celebrazioni speciali.' 
            : 'Services for exclusive events in prestigious locations: planning and organization of private events, corporate events, and special celebrations.'} 
        />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {language === 'it' ? 'Eventi' : 'Events'}
          </HeroTitle>
          <HeroSubtitle>
            {language === 'it'
              ? 'Trasforma le nostre location esclusive nel palcoscenico perfetto per il tuo evento indimenticabile.'
              : 'Transform our exclusive locations into the perfect stage for your unforgettable event.'}
          </HeroSubtitle>
          <Button variant="primary" to="/contact">
            {language === 'it' ? 'Richiedi Informazioni' : 'Request Information'}
          </Button>
        </HeroContent>
      </HeroSection>
      
      <Section padding="6rem 0">
        <Content>
          <ServiceTitle>
            {language === 'it' ? 'I Nostri Servizi per Eventi' : 'Our Event Services'}
          </ServiceTitle>
          
          <ServiceDescription>
            {language === 'it'
              ? 'Trasforma le nostre location esclusive nel palcoscenico perfetto per il tuo evento. Che si tratti di una celebrazione privata, un evento aziendale o una cerimonia speciale, il nostro team dedicato si occupa di ogni dettaglio, dalla pianificazione all\'esecuzione, per creare un\'esperienza memorabile.'
              : 'Transform our exclusive locations into the perfect stage for your event. Whether it\'s a private celebration, a corporate event, or a special ceremony, our dedicated team takes care of every detail, from planning to execution, to create a memorable experience.'}
          </ServiceDescription>
          
          <ServiceDescription>
            {language === 'it'
              ? 'Con un\'attenzione meticolosa ai dettagli e una rete di fornitori selezionati, offriamo un servizio completo che trasforma la tua visione in realtà, garantendo che ogni momento del tuo evento sia semplicemente perfetto.'
              : 'With meticulous attention to detail and a network of selected suppliers, we offer a complete service that transforms your vision into reality, ensuring that every moment of your event is simply perfect.'}
          </ServiceDescription>
          
          <FeatureList>
            <FeatureItem>
              <EventIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Celebrazioni Private' : 'Private Celebrations'}</h3>
                <p>
                  {language === 'it'
                    ? 'Dalle cene intime ai matrimoni sontuosi, organizziamo celebrazioni private in location esclusive, con un servizio personalizzato che riflette il tuo stile e le tue preferenze.'
                    : 'From intimate dinners to lavish weddings, we organize private celebrations in exclusive locations, with a personalized service that reflects your style and preferences.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <EventIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Eventi Aziendali' : 'Corporate Events'}</h3>
                <p>
                  {language === 'it'
                    ? 'Conferenze, meeting, team building o cene di gala: il nostro team progetta e realizza eventi aziendali che rafforzano la tua immagine e raggiungono i tuoi obiettivi di business.'
                    : 'Conferences, meetings, team building, or gala dinners: our team designs and creates corporate events that strengthen your image and achieve your business goals.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <EventIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Pianificazione Completa' : 'Complete Planning'}</h3>
                <p>
                  {language === 'it'
                    ? 'Dalla selezione della location al coordinamento dei fornitori, dalla gestione del budget alla supervisione in loco il giorno dell\'evento, ci occupiamo di ogni aspetto.'
                    : 'From location selection to vendor coordination, from budget management to on-site supervision on the day of the event, we take care of every aspect.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <EventIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Catering e Intrattenimento' : 'Catering and Entertainment'}</h3>
                <p>
                  {language === 'it'
                    ? 'Collaboriamo con i migliori chef e fornitori di servizi di intrattenimento per offrire esperienze culinarie e momenti di svago indimenticabili per i tuoi ospiti.'
                    : 'We collaborate with the best chefs and entertainment service providers to offer unforgettable culinary experiences and entertainment moments for your guests.'}
                </p>
              </FeatureText>
            </FeatureItem>
          </FeatureList>
          
          <CallToAction>
            <CTATitle>
              {language === 'it' ? 'Pianifica il Tuo Prossimo Evento' : 'Plan Your Next Event'}
            </CTATitle>
            <CTAText>
              {language === 'it'
                ? 'Contattaci oggi per discutere la tua visione e scoprire come possiamo trasformarla in un evento indimenticabile che superi tutte le aspettative.'
                : 'Contact us today to discuss your vision and discover how we can transform it into an unforgettable event that exceeds all expectations.'}
            </CTAText>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci Ora' : 'Contact Us Now'}
            </Button>
          </CallToAction>
        </Content>
      </Section>
    </PageContainer>
  );
};

export default Events; 
